import { Button, type ColorScheme, DropdownMenu, useTheme } from '@keboola/design';
import { Monitor, Moon, Sun } from '@keboola/design/icons';

const OPTIONS = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
] as const satisfies readonly { value: ColorScheme; label: string; icon: unknown }[];

const isColorScheme = (value: string): value is ColorScheme =>
  OPTIONS.some((option) => option.value === value);

/**
 * Light / dark / system colour scheme picker for the top bar.
 *
 * - `RadioGroup` so screen readers announce the active option.
 * - The trigger shows the resolved scheme, so under `system` it says which way the OS went.
 * - All three options are always offered: brands fall back to `[data-theme='dark']` defaults.
 */
export function ColorSchemeMenu() {
  const { scheme, resolvedScheme, setScheme } = useTheme();
  const TriggerIcon = resolvedScheme === 'dark' ? Moon : Sun;

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <Button variant="invisible" size="icon-sm" aria-label="Change colour scheme">
          <TriggerIcon />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end">
        <DropdownMenu.Label>Appearance</DropdownMenu.Label>
        <DropdownMenu.RadioGroup
          value={scheme}
          onValueChange={(next) => {
            // Radix passes a bare `string`; a re-pick would write storage for no change.
            if (isColorScheme(next) && next !== scheme) setScheme(next);
          }}
        >
          {OPTIONS.map(({ value, label, icon: Icon }) => (
            <DropdownMenu.RadioItem key={value} value={value} closeOnSelect indicatorAlign="end">
              <Icon />
              {label}
            </DropdownMenu.RadioItem>
          ))}
        </DropdownMenu.RadioGroup>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
